import { HardDrive } from 'lucide-react';
import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import PageHeader from '../../components/PageHeader';
import { PAGE } from '../../components/crm/ui';
import alertService from '../../utils/alert';

const PROVIDER_LABELS = {
  local_storage: 'Local Storage (ImageKit)', google_drive: 'Google Drive', dropbox: 'Dropbox', box: 'Box', onedrive: 'OneDrive',
};
const PROVIDER_ICONS = {
  local_storage: '💾', google_drive: '🟢', dropbox: '🔵', box: '🗂️', onedrive: '🔷',
};
const CONNECTABLE = ['google_drive', 'dropbox', 'box', 'onedrive'];
const CHUNKED_UPLOAD_THRESHOLD_BYTES = 10 * 1024 * 1024; // mirrors DocumentService.CHUNKED_UPLOAD_THRESHOLD_BYTES
const SECRET_MASK_PLACEHOLDER = '••••••••••••••••';
const APP_SETUP_HINT = {
  google_drive: 'Google Cloud Console → APIs & Services → Credentials → OAuth client ID (Web application).',
  dropbox: 'Dropbox App Console → your app → App key / App secret.',
  box: 'Box Developer Console → your app → OAuth 2.0 Credentials.',
  onedrive: 'Azure Portal → App registrations → your app → Client ID/Secret + Tenant ID (use "common" for personal + work accounts).',
};

function formatBytes(n) {
  if (!n) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0; let v = n;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(1)} ${units[i]}`;
}

export default function StoragePage() {
  const [providers, setProviders] = useState({ supported: [], connected: [], configured: {}, activeProvider: 'local_storage' });
  const [usage, setUsage] = useState([]);
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [busy, setBusy] = useState('');
  const [banner, setBanner] = useState(null); // { type: 'error'|'success', message }
  const [versionsFor, setVersionsFor] = useState(null); // { documentId, fileName, versions }
  const [newFolderName, setNewFolderName] = useState('');
  const [activityLog, setActivityLog] = useState(null); // null = not yet loaded, [] = loaded empty
  const [showActivity, setShowActivity] = useState(false);
  const [appCredsProvider, setAppCredsProvider] = useState(null); // which provider's "own app" form is open
  const [appCredsForm, setAppCredsForm] = useState({ clientId: '', clientSecret: '', tenantId: '' });
  const [appCredsSecretSet, setAppCredsSecretSet] = useState(false);
  const [appCredsLoading, setAppCredsLoading] = useState(false);
  const [appCredsSaving, setAppCredsSaving] = useState(false);
  const [imagekitOpen, setImagekitOpen] = useState(false);
  const [imagekitForm, setImagekitForm] = useState({ imagekitUrlEndpoint: '', imagekitPublicKey: '', imagekitPrivateKey: '' });
  const [imagekitHasPrivateKey, setImagekitHasPrivateKey] = useState(false);
  const [imagekitLoading, setImagekitLoading] = useState(false);
  const [imagekitSaving, setImagekitSaving] = useState(false);

  const loadProviders = useCallback(async () => {
    const { data } = await api.get('/storage/providers');
    setProviders(data.data);
    const { data: usageData } = await api.get('/storage/usage');
    setUsage(usageData.data || []);
  }, []);

  const loadFolders = useCallback(async () => {
    const { data } = await api.get('/storage/folders');
    setFolders(data.data || []);
  }, []);

  const loadFiles = useCallback(async (folderId) => {
    const { data } = await api.get('/storage/files', { params: { folderId } });
    setFiles(data.items || []);
  }, []);

  const loadActivity = useCallback(async () => {
    const { data } = await api.get('/storage/activity', { params: { limit: 30 } });
    setActivityLog(data.items || []);
  }, []);

  const toggleActivity = () => {
    const next = !showActivity;
    setShowActivity(next);
    if (next && activityLog === null) loadActivity();
  };

  useEffect(() => { loadProviders(); loadFolders(); }, [loadProviders, loadFolders]);
  useEffect(() => {
    api.get('/auth/profile').then(({ data }) => setImagekitHasPrivateKey(!!data.company?.hasImagekitPrivateKey)).catch(() => {});
  }, []);
  useEffect(() => { if (selectedFolder) loadFiles(selectedFolder._id); else setFiles([]); }, [selectedFolder, loadFiles]);

  // The OAuth callback redirects back here with ?connected=<provider> or ?error=<message> —
  // surface it once, then strip the query string so a refresh doesn't re-show the banner.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const connected = params.get('connected');
    const err = params.get('error');
    if (connected) {
      setBanner({ type: 'success', message: `${PROVIDER_LABELS[connected] || connected} connected successfully.` });
      loadProviders();
    } else if (err) {
      setBanner({ type: 'error', message: err });
    }
    if (connected || err) window.history.replaceState({}, '', window.location.pathname);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connectedMap = Object.fromEntries((providers.connected || []).map(c => [c.provider, c]));

  const connect = async (provider) => {
    setBusy(provider); setBanner(null);
    try {
      const { data } = await api.get(`/storage/oauth/${provider}/authorize`);
      window.location.href = data.authorizeUrl;
    } catch (err) {
      setBanner({ type: 'error', message: err.response?.data?.error || 'Failed to start connection' });
      setBusy('');
    }
  };

  const disconnect = async (provider) => {
    setBusy(provider);
    try { await api.post(`/storage/providers/${provider}/disconnect`); await loadProviders(); }
    finally { setBusy(''); }
  };

  const reconnect = async (provider) => {
    setBusy(provider);
    try {
      const { data } = await api.post(`/storage/providers/${provider}/reconnect`);
      if (!data.success) alertService.error('Error', `Test failed: ${data.error}`);
      await loadProviders();
    } finally { setBusy(''); }
  };

  // Normally a single platform-wide OAuth app (registered once by a SuperAdmin under Admin
  // Settings > Storage OAuth Apps) covers every tenant — a customer just clicks Connect and
  // consents. This panel is only for a tenant that wants an OPTIONAL override: their own
  // Google Cloud/Dropbox/Box/Azure AD app instead of the shared platform one. Not required.
  const toggleAppCreds = async (provider) => {
    if (appCredsProvider === provider) { setAppCredsProvider(null); return; }
    setAppCredsProvider(provider);
    setAppCredsForm({ clientId: '', clientSecret: '', tenantId: '' });
    setAppCredsSecretSet(false);
    setAppCredsLoading(true);
    try {
      const { data } = await api.get(`/storage/providers/${provider}/app-credentials`);
      setAppCredsForm({ clientId: data.data.clientId || '', clientSecret: '', tenantId: data.data.tenantId || '' });
      setAppCredsSecretSet(!!data.data.clientSecretSet);
    } catch (err) {
      setBanner({ type: 'error', message: err.response?.data?.error || 'Failed to load app credentials' });
      setAppCredsProvider(null);
    } finally {
      setAppCredsLoading(false);
    }
  };

  const saveAppCreds = async (provider) => {
    if (!appCredsForm.clientId.trim()) {
      setBanner({ type: 'error', message: 'Client ID is required' });
      return;
    }
    setAppCredsSaving(true);
    try {
      await api.put(`/storage/providers/${provider}/app-credentials`, {
        clientId: appCredsForm.clientId.trim(),
        ...(appCredsForm.clientSecret.trim() ? { clientSecret: appCredsForm.clientSecret.trim() } : {}),
        ...(provider === 'onedrive' ? { tenantId: appCredsForm.tenantId.trim() || 'common' } : {}),
      });
      setBanner({ type: 'success', message: `${PROVIDER_LABELS[provider]} app credentials saved.` });
      setAppCredsProvider(null);
      await loadProviders();
    } catch (err) {
      setBanner({ type: 'error', message: err.response?.data?.error || 'Failed to save app credentials' });
    } finally {
      setAppCredsSaving(false);
    }
  };

  // "Local Storage" is backed by ImageKit — the platform's shared ImageKit account has its
  // own plan limits (e.g. a low per-file size cap), which every tenant is stuck on until they
  // bring their own ImageKit keys here. Reuses the existing safe /auth/profile endpoint
  // (masked hasImagekitPrivateKey, never the raw key) rather than the legacy /settings/imagekit
  // route buried in Organization Settings.
  const toggleImagekit = async () => {
    if (imagekitOpen) { setImagekitOpen(false); return; }
    setImagekitOpen(true);
    setImagekitLoading(true);
    try {
      const { data } = await api.get('/auth/profile');
      setImagekitForm({
        imagekitUrlEndpoint: data.company?.imagekitUrlEndpoint || '',
        imagekitPublicKey: data.company?.imagekitPublicKey || '',
        imagekitPrivateKey: '',
      });
      setImagekitHasPrivateKey(!!data.company?.hasImagekitPrivateKey);
    } catch (err) {
      setBanner({ type: 'error', message: err.response?.data?.error || 'Failed to load ImageKit settings' });
      setImagekitOpen(false);
    } finally {
      setImagekitLoading(false);
    }
  };

  const saveImagekit = async () => {
    setImagekitSaving(true);
    try {
      await api.put('/auth/profile', {
        imagekitUrlEndpoint: imagekitForm.imagekitUrlEndpoint.trim(),
        imagekitPublicKey: imagekitForm.imagekitPublicKey.trim(),
        ...(imagekitForm.imagekitPrivateKey.trim() ? { imagekitPrivateKey: imagekitForm.imagekitPrivateKey.trim() } : {}),
      });
      setBanner({ type: 'success', message: 'ImageKit settings saved.' });
      setImagekitOpen(false);
    } catch (err) {
      setBanner({ type: 'error', message: err.response?.data?.error || 'Failed to save ImageKit settings' });
    } finally {
      setImagekitSaving(false);
    }
  };

  const createFolder = async () => {
    if (!newFolderName.trim()) return;
    await api.post('/storage/folders', {
      name: newFolderName.trim(), parentFolderId: selectedFolder?._id || null, module: selectedFolder?.module || 'root',
    });
    setNewFolderName('');
    await loadFolders();
  };

  const uploadChunked = async (file, folder) => {
    const { data } = await api.post('/storage/upload/session', {
      fileName: file.name, mimeType: file.type, totalSize: file.size, folderId: folder._id, module: folder.module || 'root',
    });
    const { sessionId, chunkSize } = data.data;
    let offset = 0;

    while (offset < file.size) {
      const chunkStartOffset = offset;
      const chunk = await file.slice(offset, Math.min(offset + chunkSize, file.size)).arrayBuffer();

      let attempt = 0;
      let advancedPastThisChunk = false;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        try {
          const res = await api.put(`/storage/upload/session/${sessionId}/chunk`, chunk, {
            headers: { 'Content-Type': 'application/octet-stream' },
          });
          if (res.data.done) return;
          offset = res.data.bytesUploaded; // authoritative — trust the server's count, not our own arithmetic
          setUploadProgress(Math.round((offset / file.size) * 100));
          break;
        } catch (err) {
          // A dropped connection here doesn't mean the chunk failed — the server may have
          // already saved it (or, for the last chunk, finished the whole upload) before the
          // response made it back. Blindly retrying the same bytes then hits "Upload session
          // not found or already completed" on an already-finished session. Check the
          // session's actual server-side status before deciding this attempt truly failed.
          try {
            const { data: statusRes } = await api.get(`/storage/upload/session/${sessionId}`);
            const session = statusRes.data;
            if (session.status === 'completed') return; // already finished — nothing more to do
            if (session.status === 'failed' || session.status === 'aborted') {
              throw new Error(session.lastError || 'Upload failed');
            }
            if (session.bytesUploaded > chunkStartOffset) {
              // This chunk was actually accepted server-side before the connection dropped —
              // move on to the next chunk instead of resending these same bytes.
              offset = session.bytesUploaded;
              setUploadProgress(Math.round((offset / file.size) * 100));
              advancedPastThisChunk = true;
              break;
            }
          } catch {
            // Session status check itself failed (e.g. 404) — fall through to the normal
            // retry-then-give-up path below using the original error.
          }

          attempt += 1;
          if (attempt >= 3) {
            await api.delete(`/storage/upload/session/${sessionId}`).catch(() => {});
            throw err;
          }
          await new Promise((resolve) => setTimeout(resolve, 1000 * attempt)); // brief backoff, then retry
        }
      }
      if (advancedPastThisChunk) continue;
    }
  };

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedFolder) return;
    setUploading(true);
    setUploadProgress(0);
    try {
      if (file.size > CHUNKED_UPLOAD_THRESHOLD_BYTES) {
        await uploadChunked(file, selectedFolder);
      } else {
        const form = new FormData();
        form.append('file', file);
        form.append('folderId', selectedFolder._id);
        form.append('module', selectedFolder.module || 'root');
        await api.post('/storage/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      await loadFiles(selectedFolder._id);
    } catch (err) {
      alertService.error('Error', err.response?.data?.error || 'Upload failed');
    } finally { setUploading(false); setUploadProgress(0); e.target.value = ''; }
  };

  const deleteFile = async (id) => {
    if (!(await alertService.confirm('Delete?', 'Delete this file?', 'danger', 'Delete'))) return;
    await api.delete(`/storage/files/${id}`);
    await loadFiles(selectedFolder._id);
  };

  const uploadNewVersion = async (docId, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(`version-${docId}`);
    try {
      const form = new FormData();
      form.append('file', file);
      await api.post(`/storage/files/${docId}/version`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
      await loadFiles(selectedFolder._id);
    } catch (err) {
      alertService.error('Error', err.response?.data?.error || 'Failed to upload new version');
    } finally { setBusy(''); e.target.value = ''; }
  };

  const openVersions = async (doc) => {
    const { data } = await api.get(`/storage/files/${doc._id}/versions`);
    setVersionsFor({ documentId: doc._id, fileName: doc.fileName, versions: data.data || [] });
  };

  const roots = folders.filter(f => !f.parentFolderId);
  const childrenOf = (id) => folders.filter(f => String(f.parentFolderId) === String(id));

  const renderTree = (list, depth = 0) => list.map(f => (
    <div key={f._id}>
      <button
        onClick={() => setSelectedFolder(f)}
        className={`w-full text-left text-[13px] px-2 py-1.5 rounded flex items-center gap-2 hover:bg-[#F3F2F2] ${selectedFolder?._id === f._id ? 'bg-[#EEF4FF] text-[#0176D3] font-medium' : 'text-[#444444]'}`}
        style={{ paddingLeft: `${8 + depth * 16}px` }}
      >
        <span>📁</span>{f.name}
      </button>
      {renderTree(childrenOf(f._id), depth + 1)}
    </div>
  ));

  return (
    <div className={PAGE}>
      <PageHeader
        title="Storage"
        icon={HardDrive}
        subtitle="Connect your own cloud storage — every module's documents live in one place, isolated per account."
        actions={<button onClick={toggleActivity} className="h-8 text-[13px] font-semibold px-3 rounded bg-white border border-[#C9C9C9] text-[#0176D3] hover:bg-[#F3F2F2] whitespace-nowrap">{showActivity ? 'Hide Activity Log' : 'View Activity Log'}</button>}
      />

      {showActivity && (
        <div className="bg-white border border-[#E5E5E5] rounded-lg p-4 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
          <h3 className="text-sm font-semibold text-[#2E2E2E] mb-3">Recent Activity</h3>
          {activityLog === null ? (
            <p className="text-sm text-[#706E6B]">Loading…</p>
          ) : activityLog.length === 0 ? (
            <p className="text-sm text-[#706E6B]">No activity recorded yet.</p>
          ) : (
            <div className="divide-y divide-[#F3F2F2] max-h-80 overflow-y-auto">
              {activityLog.map(entry => (
                <div key={entry._id} className="flex items-center justify-between py-2 text-sm">
                  <div className="flex items-center gap-2 text-[#444444]">
                    <span className="text-xs px-2 py-0.5 rounded-full bg-[#F3F2F2] text-[#5C5C5C]">{entry.action.replace(/_/g, ' ')}</span>
                    <span className="text-[#706E6B]">{PROVIDER_LABELS[entry.provider] || entry.provider}</span>
                    {entry.actorId?.name && <span className="text-[#706E6B]">by {entry.actorId.name}</span>}
                  </div>
                  <span className="text-xs text-[#706E6B]">{new Date(entry.createdAt).toLocaleString()}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {banner && (
        <div className={`rounded-lg px-4 py-3 text-sm flex items-center justify-between ${banner.type === 'error' ? 'bg-[#FEF1EE] text-[#BA0517]' : 'bg-[#EBF7E6] text-[#2E844A]'}`}>
          <span>{banner.message}</span>
          <button onClick={() => setBanner(null)} className="text-xs opacity-70 hover:opacity-100">Dismiss</button>
        </div>
      )}

      {/* Provider cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {['local_storage', ...CONNECTABLE].map((provider) => {
          const rec = connectedMap[provider];
          const isActive = providers.activeProvider === provider;
          const usageRec = usage.find(u => u.provider === provider);
          const isConfigured = providers.configured?.[provider] !== false;
          return (
            <div key={provider} className={`bg-white border rounded-lg p-4 flex flex-col gap-3 ${isActive ? 'border-[#0176D3] ring-1 ring-[#D8E6FE]' : 'border-[#E5E5E5]'} shadow-[0_1px_3px_rgba(0,0,0,0.08)]`}>
              <div className="flex items-center gap-3">
                <span className="text-2xl">{PROVIDER_ICONS[provider]}</span>
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-[#2E2E2E]">{PROVIDER_LABELS[provider]}</h3>
                  {provider === 'local_storage' ? (
                    <span className="text-xs text-[#706E6B]">
                      Always available — default tier{imagekitHasPrivateKey ? ' · Your own ImageKit account' : ' · Shared platform account'}
                    </span>
                  ) : !isConfigured ? (
                    <span className="text-xs px-2 py-0.5 rounded-full text-[#DD7A01] bg-[#FEF1E5]">Setup required</span>
                  ) : rec ? (
                    <span className={`text-xs px-2 py-0.5 rounded-full ${rec.lastTestStatus === 'ok' ? 'text-[#2E844A] bg-[#EBF7E6]' : rec.lastTestStatus === 'error' ? 'text-[#BA0517] bg-[#FEF1EE]' : 'text-[#706E6B] bg-[#F7F9FB]'}`}>
                      {rec.lastTestStatus === 'ok' ? 'Connected' : rec.lastTestStatus === 'error' ? 'Error' : 'Untested'}
                    </span>
                  ) : (
                    <span className="text-xs text-[#706E6B]">Not connected</span>
                  )}
                </div>
                {isActive && <span className="text-xs font-medium text-[#0176D3]">Active</span>}
              </div>
              {usageRec && (
                <div className="text-xs text-[#706E6B]">{usageRec.fileCount} files · {formatBytes(usageRec.totalBytes)}</div>
              )}
              <div className="flex flex-wrap gap-2">
                {provider === 'local_storage' ? (
                  <button onClick={toggleImagekit} className="text-[13px] px-3 py-1.5 rounded whitespace-nowrap border border-[#E5E5E5] hover:bg-[#F7F9FB]">
                    {imagekitOpen ? 'Close' : imagekitHasPrivateKey ? 'Edit ImageKit keys' : 'Configure your own ImageKit'}
                  </button>
                ) : (
                  <>
                    {isConfigured && rec ? (
                      <>
                        <button onClick={() => reconnect(provider)} disabled={busy === provider} className="text-[13px] px-3 py-1.5 rounded border border-[#E5E5E5] hover:bg-[#F7F9FB] disabled:opacity-50">Test</button>
                        <button onClick={() => disconnect(provider)} disabled={busy === provider} className="text-[13px] px-3 py-1.5 rounded border border-[#FDDDE3] text-[#BA0517] hover:bg-[#FEF1EE] disabled:opacity-50">Disconnect</button>
                      </>
                    ) : isConfigured ? (
                      <button onClick={() => connect(provider)} disabled={busy === provider} className="text-[13px] px-3 py-1.5 rounded bg-[#0176D3] text-white hover:bg-[#014486] disabled:opacity-50">
                        {busy === provider ? 'Redirecting…' : 'Connect'}
                      </button>
                    ) : null}
                    <button onClick={() => toggleAppCreds(provider)} className="text-[13px] px-3 py-1.5 rounded border border-[#E5E5E5] hover:bg-[#F7F9FB]">
                      {appCredsProvider === provider ? 'Close' : isConfigured ? 'Edit app credentials' : 'Set up your app'}
                    </button>
                  </>
                )}
              </div>

              {provider === 'local_storage' && imagekitOpen && (
                <div className="mt-1 pt-3 border-t border-[#F3F2F2] space-y-2">
                  <p className="text-xs text-[#706E6B]">
                    Local Storage runs on ImageKit. The shared platform ImageKit account has its own plan
                    limits (e.g. a low per-file size cap) — bring your own ImageKit account below to lift them.
                  </p>
                  <p className="text-xs text-[#706E6B]">ImageKit Dashboard → Developer Options → API Keys.</p>
                  {imagekitLoading ? (
                    <p className="text-xs text-[#706E6B]">Loading…</p>
                  ) : (
                    <>
                      <input
                        value={imagekitForm.imagekitUrlEndpoint}
                        onChange={e => setImagekitForm(f => ({ ...f, imagekitUrlEndpoint: e.target.value }))}
                        placeholder="URL Endpoint (https://ik.imagekit.io/your_id)"
                        className="w-full text-[13px] border border-[#D8DDE6] rounded px-2 py-1.5"
                      />
                      <input
                        value={imagekitForm.imagekitPublicKey}
                        onChange={e => setImagekitForm(f => ({ ...f, imagekitPublicKey: e.target.value }))}
                        placeholder="Public Key"
                        className="w-full text-[13px] border border-[#D8DDE6] rounded px-2 py-1.5"
                      />
                      <input
                        type="password"
                        value={imagekitForm.imagekitPrivateKey}
                        onChange={e => setImagekitForm(f => ({ ...f, imagekitPrivateKey: e.target.value }))}
                        placeholder={imagekitHasPrivateKey ? SECRET_MASK_PLACEHOLDER : 'Private Key'}
                        className="w-full text-[13px] border border-[#D8DDE6] rounded px-2 py-1.5"
                      />
                      {imagekitHasPrivateKey && (
                        <p className="text-xs text-[#706E6B]">A private key is already saved — leave blank to keep it.</p>
                      )}
                      <button
                        onClick={saveImagekit}
                        disabled={imagekitSaving}
                        className="text-[13px] px-3 py-1.5 rounded whitespace-nowrap bg-[#0176D3] text-white hover:bg-[#014486] disabled:opacity-50"
                      >
                        {imagekitSaving ? 'Saving…' : 'Save ImageKit keys'}
                      </button>
                    </>
                  )}
                </div>
              )}

              {appCredsProvider === provider && (
                <div className="mt-1 pt-3 border-t border-[#F3F2F2] space-y-2">
                  <p className="text-xs text-[#706E6B]">
                    Bring your own {PROVIDER_LABELS[provider]} OAuth app — your tenant's documents flow through
                    credentials only your company controls, not a shared platform app.
                  </p>
                  <p className="text-xs text-[#706E6B]">{APP_SETUP_HINT[provider]}</p>
                  {appCredsLoading ? (
                    <p className="text-xs text-[#706E6B]">Loading…</p>
                  ) : (
                    <>
                      <input
                        value={appCredsForm.clientId}
                        onChange={e => setAppCredsForm(f => ({ ...f, clientId: e.target.value }))}
                        placeholder="Client ID / App Key"
                        className="w-full text-[13px] border border-[#D8DDE6] rounded px-2 py-1.5"
                      />
                      <input
                        type="password"
                        value={appCredsForm.clientSecret}
                        onChange={e => setAppCredsForm(f => ({ ...f, clientSecret: e.target.value }))}
                        placeholder={appCredsSecretSet ? SECRET_MASK_PLACEHOLDER : 'Client Secret / App Secret'}
                        className="w-full text-[13px] border border-[#D8DDE6] rounded px-2 py-1.5"
                      />
                      {appCredsSecretSet && (
                        <p className="text-xs text-[#706E6B]">A secret is already saved — leave blank to keep it.</p>
                      )}
                      {provider === 'onedrive' && (
                        <input
                          value={appCredsForm.tenantId}
                          onChange={e => setAppCredsForm(f => ({ ...f, tenantId: e.target.value }))}
                          placeholder="Azure AD Tenant ID (or 'common')"
                          className="w-full text-[13px] border border-[#D8DDE6] rounded px-2 py-1.5"
                        />
                      )}
                      <button
                        onClick={() => saveAppCreds(provider)}
                        disabled={appCredsSaving}
                        className="text-[13px] px-3 py-1.5 rounded bg-[#0176D3] text-white hover:bg-[#014486] disabled:opacity-50"
                      >
                        {appCredsSaving ? 'Saving…' : 'Save app credentials'}
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Folder tree + file browser */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white border border-[#E5E5E5] rounded-lg p-4 space-y-2 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
          <h3 className="text-sm font-semibold text-[#2E2E2E] mb-2">Folders</h3>
          <div className="max-h-96 overflow-y-auto">{renderTree(roots)}</div>
          <div className="flex gap-2 pt-2 border-t border-[#F3F2F2]">
            <input
              value={newFolderName} onChange={e => setNewFolderName(e.target.value)}
              placeholder="New folder name" className="flex-1 text-[13px] border border-[#D8DDE6] rounded px-2 py-1.5"
            />
            <button onClick={createFolder} className="text-[13px] px-3 py-1.5 rounded whitespace-nowrap bg-[#F3F2F2] hover:bg-[#E5E5E5]">Add</button>
          </div>
        </div>

        <div className="md:col-span-2 bg-white border border-[#E5E5E5] rounded-lg p-4 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-[#2E2E2E]">{selectedFolder ? selectedFolder.path : 'Select a folder'}</h3>
            {selectedFolder && (
              <label className="text-[12px] px-3 py-1.5 rounded-lg bg-[#0176D3] text-white hover:bg-[#0176D3] cursor-pointer">
                {uploading ? (uploadProgress > 0 ? `Uploading… ${uploadProgress}%` : 'Uploading…') : 'Upload File'}
                <input type="file" className="hidden" onChange={uploadFile} disabled={uploading} />
              </label>
            )}
          </div>
          {!selectedFolder ? (
            <p className="text-sm text-[#706E6B]">Choose a folder on the left to view and upload files.</p>
          ) : files.length === 0 ? (
            <p className="text-sm text-[#706E6B]">No files in this folder yet.</p>
          ) : (
            <div className="divide-y divide-[#F3F2F2]">
              {files.map(f => (
                <div key={f._id} className="flex items-center justify-between py-2">
                  <div className="flex items-center gap-2 text-sm text-[#444444]">
                    <span>📄</span>
                    <a href={f.previewUrl || f.downloadUrl || '#'} target="_blank" rel="noreferrer" className="hover:underline">{f.fileName}</a>
                    <span className="text-xs text-[#706E6B]">{formatBytes(f.size)}</span>
                    {f.currentVersion > 1 && (
                      <span className="text-xs px-1.5 py-0.5 rounded-full bg-[#F3F2F2] text-[#706E6B]">v{f.currentVersion}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <a
                      // Link straight to the provider's own public URL when there is
                      // one (ImageKit/local_storage) instead of proxying through our
                      // server — only falls back to the authenticated proxy endpoint
                      // for providers whose files aren't publicly reachable.
                      href={f.downloadUrl || `/api/storage/files/${f._id}/download`}
                      target={f.downloadUrl ? '_blank' : undefined}
                      rel={f.downloadUrl ? 'noopener noreferrer' : undefined}
                      download={f.downloadUrl ? f.fileName : undefined}
                      className="text-xs text-[#706E6B] hover:text-[#444444]"
                    >Download</a>
                    <button onClick={() => openVersions(f)} className="text-xs text-[#706E6B] hover:text-[#444444]">Versions</button>
                    <label className="text-[12px] text-[#5C5C5C] hover:text-[#444444] cursor-pointer">
                      {busy === `version-${f._id}` ? 'Uploading…' : 'New version'}
                      <input type="file" className="hidden" onChange={(e) => uploadNewVersion(f._id, e)} disabled={busy === `version-${f._id}`} />
                    </label>
                    <button onClick={() => deleteFile(f._id)} className="text-xs text-[#BA0517] hover:text-[#BA0517]">Delete</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {versionsFor && (
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center sm:p-4 bg-black/40">
          <div className="bg-white p-4 sm:p-6 w-full max-w-md space-y-3 rounded-t-lg sm:rounded-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <h3 className="text-[16px] font-bold text-[#2E2E2E]">Versions — {versionsFor.fileName}</h3>
              <button onClick={() => setVersionsFor(null)} className="text-sm text-[#706E6B] hover:text-[#5C5C5C]">✕</button>
            </div>
            {versionsFor.versions.length === 0 ? (
              <p className="text-sm text-[#706E6B]">No version history yet.</p>
            ) : (
              <div className="divide-y divide-[#F3F2F2]">
                {versionsFor.versions.map(v => (
                  <div key={v._id} className="flex items-center justify-between py-2 text-sm">
                    <span>Version {v.versionNumber}</span>
                    <span className="text-xs text-[#706E6B]">{formatBytes(v.size)} · {new Date(v.createdAt).toLocaleString()}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
